import './StreamingConstraints.scss';
import * as Dfns from 'date-fns/fp';
import { useEffect, useMemo, useState } from 'react';
import {
  StreamingConstraintData,
  StreamingConstraintRule
} from '@blisspointmedia/bpm-types/dist/OptimizationConstraints';
import { SelectorOption, SaveChangesInput } from '@Components/ModalEditWidget/modal-edit-constants';
import { ModalEditWidget } from '@Components/ModalEditWidget/ModalEditWidget';
import { ConstraintSaveBar } from '@Components/ConstraintSaveBar/ConstraintSaveBar';
import { useConstraintDraft } from '@src/utils/hooks/useConstraintDraft';
import { applyTableChanges, withIndexes } from '@src/utils/optimization-utils';
import { Page } from '@Components/Page/Page';
import { FullPageSpinner } from '@Components/Spinner/Spinner';
import { useSetError } from '../../redux/modals';
import { awaitJSON, StreamingOptimizationsLambdaFetch } from '../../utils/fetch-utils';
import { StreamingPlacementAndDerivedNetwork } from '../StreamingOptimizations/streaming-optimizations-constants';

const PRETTY_DATE_FORMAT = 'yyyy-MM-dd hh:mma';

const persistConstraint = async (data: StreamingConstraintData): Promise<StreamingConstraintData> =>
  awaitJSON<StreamingConstraintData>(
    await StreamingOptimizationsLambdaFetch('/constraints', {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: { ...data, streamingType: 'streaming', start: null, end: null }
    })
  );

export const StreamingConstraints = () => {
  const streamingType = 'streaming';
  const setError = useSetError();
  const [initialData, setInitialData] = useState<StreamingConstraintData>();

  useEffect(() => {
    if (!initialData) {
      (async () => {
        try {
          const getGlobalConstraintResponse = await StreamingOptimizationsLambdaFetch(
            '/constraints',
            {
              params: {
                company: 'global',
                name: 'global',
                streamingType
              }
            }
          );
          const globalConstraints = await awaitJSON<StreamingConstraintData>(
            getGlobalConstraintResponse
          );
          for (const table of ['maximum', 'avoid'] as const) {
            globalConstraints.constraints[table] = withIndexes(
              (globalConstraints.constraints[table] ?? [])
                .map(row => ({
                  ...row,
                  lastmodified: row.lastmodified || '2020-01-01T00:00:00-08:00'
                }))
                .sort((a, b) => a.lastmodified.localeCompare(b.lastmodified))
            );
          }
          setInitialData(globalConstraints);
        } catch (e) {
          setError({
            message: `Failed to get constraint data. Error: ${e.message}`,
            reportError: e
          });
        }
      })();
    }
  }, [initialData, streamingType, setError]);

  const { data, update, discard, save, changeCount, canSave, saving } = useConstraintDraft({
    identity: 'streaming-global',
    initialData,
    persist: persistConstraint
  });

  const applyTable = (
    section: 'maximum' | 'avoid',
    changes: SaveChangesInput<StreamingConstraintRule>
  ) => {
    if (!data) return;
    update({
      ...data,
      constraints: {
        ...data.constraints,
        [section]: withIndexes(
          applyTableChanges(
            data.constraints[section] ?? [],
            changes.addedData,
            changes.modifiedData,
            changes.deletedData
          )
        )
      }
    });
  };

  const maximumHeaders = [
    {
      type: 'select',
      field: 'publishers',
      label: 'Publisher',
      isMulti: true,
      creatable: false,
      options: 'publishers',
      clearKeys: [],
      modalRow: 0,
      modalFlex: 2
    },
    {
      type: 'select',
      field: 'placements',
      label: 'Network + Content',
      isMulti: true,
      creatable: false,
      options: 'placements',
      clearKeys: [],
      modalRow: 1,
      modalFlex: 1
    },
    {
      type: 'currency',
      field: 'spend',
      omitEmptyNumber: true,
      label: 'Spend',
      width: 120,
      modalRow: 3,
      modalFlex: 1
    },
    {
      type: 'text',
      field: 'notes',
      label: 'Notes',
      flex: 1,
      modalRow: 4,
      modalFlex: 1
    },
    {
      type: 'timestamp',
      field: 'lastmodified',
      label: 'Last Modified',
      width: 200,
      modalRow: 5,
      modalFlex: 1,
      readOnly: true
    }
  ];

  const avoidHeaders = [
    {
      type: 'select',
      field: 'publishers',
      label: 'Publisher',
      isMulti: true,
      creatable: false,
      options: 'publishers',
      clearKeys: [],
      modalRow: 0,
      modalFlex: 2
    },
    {
      type: 'select',
      field: 'placements',
      label: 'Network + Content',
      isMulti: true,
      creatable: false,
      options: 'placements',
      clearKeys: [],
      modalRow: 1,
      modalFlex: 1
    },
    {
      type: 'select',
      field: 'length',
      label: 'Length',
      searchable: false,
      options: 'length',
      modalRow: 1,
      modalWidth: 100
    },
    {
      type: 'text',
      field: 'notes',
      label: 'Notes',
      flex: 1,
      modalRow: 2,
      modalFlex: 1
    },
    {
      type: 'timestamp',
      field: 'lastmodified',
      label: 'Last Modified',
      width: 200,
      modalRow: 3,
      modalFlex: 1,
      readOnly: true
    }
  ];

  const lastMod = useMemo(
    () =>
      data
        ? `Last Modified: ${(data.lastuser ?? '').replace(/@.*/, '')} at ${Dfns.format(PRETTY_DATE_FORMAT)(Dfns.parseISO(data.lastmodified))}`
        : '',
    [data]
  );

  const [placements, setPlacements] = useState<StreamingPlacementAndDerivedNetwork[]>([]);
  const [placementsDownloaded, setPlacementsDownloaded] = useState(false);

  useEffect(() => {
    if (placements.length === 0 && !placementsDownloaded) {
      (async () => {
        try {
          const getDerivedNetworksResponse = await StreamingOptimizationsLambdaFetch(
            '/getPlacementsAndDerivedNetworks',
            {
              params: { mediaType: streamingType }
            }
          );
          const placementsData: StreamingPlacementAndDerivedNetwork[] = await awaitJSON(
            getDerivedNetworksResponse
          );
          setPlacements(placementsData);
          setPlacementsDownloaded(true);
        } catch (e) {
          setError({
            message: `Failed to network data. Error: ${e.message}`,
            reportError: e
          });
        }
      })();
    }
  }, [setError, placements, streamingType, placementsDownloaded]);

  const placementOptions: SelectorOption[] = useMemo(
    () =>
      [...new Set(placements.flatMap(p => (p.placement != null ? [p.placement] : [])))]
        .sort()
        .map(e => ({ label: e, value: e })),
    [placements]
  );

  const publisherOptions: SelectorOption[] = useMemo(
    () =>
      [...new Set(placements.flatMap(p => (p.publisher != null ? [p.publisher] : [])))]
        .sort()
        .map(e => ({ label: e, value: e })),
    [placements]
  );

  const selectorOptions: Record<string, SelectorOption[]> = useMemo(
    () => ({
      length: ['15', '30', '60'].map(e => ({ label: e, value: e })),
      placements: placementOptions,
      publishers: publisherOptions
    }),
    [placementOptions, publisherOptions]
  );

  return (
    <Page
      title='Streaming Global Constraints'
      minHeight='600'
      actions={data ? <div data-testid='last-modified'>{lastMod}</div> : undefined}
      pageType='Streaming Global Constraints'
      subHeader={
        data && (
          <ConstraintSaveBar
            changeCount={changeCount}
            disabled={!canSave}
            saving={saving}
            onSave={save}
            onDiscard={discard}
          />
        )
      }
    >
      {data?.constraints ? (
        <div className='streaming-constraints'>
          <ModalEditWidget
            headers={maximumHeaders}
            name='Maximum constraints'
            initialTableData={data.constraints.maximum ?? []}
            filterBar
            saveOnEdit
            saveButtonText='Apply'
            readOnly={saving}
            onSaveModal={changes => applyTable('maximum', changes)}
            selectorOptions={selectorOptions}
            className='constraint-table'
            dataTestId='maximum-constraints-table'
          />
          <ModalEditWidget
            headers={avoidHeaders}
            name='Avoid constraints'
            initialTableData={data.constraints.avoid}
            filterBar
            saveOnEdit
            saveButtonText='Apply'
            readOnly={saving}
            onSaveModal={changes => applyTable('avoid', changes)}
            selectorOptions={selectorOptions}
            className='constraint-table'
            dataTestId='avoid-constraints-table'
          />
        </div>
      ) : (
        <FullPageSpinner />
      )}
    </Page>
  );
};
